
import React, { useEffect } from 'react';
import { useEnhancedAuth } from '@/hooks/useEnhancedAuth';
import { AlertTriangle, Shield, Clock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface EnhancedSecurityWrapperProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
}

const EnhancedSecurityWrapper: React.FC<EnhancedSecurityWrapperProps> = ({ 
  children, 
  requireAdmin = false, 
  requireSuperAdmin = false 
}) => {
  const { 
    isSecure, 
    securityCheck, 
    adminUser, 
    isSuperAdmin, 
    sessionExpiry,
    performSecurityCheck 
  } = useEnhancedAuth({ requireAdmin, requireSuperAdmin });

  useEffect(() => {
    // Enhanced session validation with security logging
    const validateSession = async () => {
      await performSecurityCheck();
    };

    validateSession();
    const interval = setInterval(validateSession, 2 * 60 * 1000); // Every 2 minutes

    return () => clearInterval(interval);
  }, [performSecurityCheck]);

  // Loading state
  if (securityCheck === 'pending') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Validating security credentials...</p>
        </div>
      </div>
    );
  }

  // Security check failed
  if (securityCheck === 'failed' || !isSecure) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-red-50">
        <Card className="p-8 max-w-md mx-auto text-center border-red-200">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600 mb-4">
            {requireSuperAdmin 
              ? 'Super admin access required for this section.'
              : requireAdmin 
                ? 'Admin access required for this section.'
                : 'Security validation failed. Please log in again.'
            }
          </p>
          <Button 
            onClick={() => window.location.href = '/admin/login'}
            className="bg-red-600 hover:bg-red-700"
          >
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  // Security status indicator
  const formatTimeUntilExpiry = () => {
    if (!sessionExpiry) return 'Unknown';
    const now = new Date();
    const diff = sessionExpiry.getTime() - now.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };

  return (
    <div className="min-h-screen">
      {/* Security Status Bar */}
      <div className="bg-green-50 border-b border-green-200 px-4 py-2">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Shield className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-800 font-medium">Security Active</span>
            </div>
            {adminUser && (
              <Badge variant={isSuperAdmin ? "destructive" : "secondary"}>
                {isSuperAdmin ? 'Super Admin' : 'Admin'}
              </Badge>
            )}
          </div>
          <div className="flex items-center space-x-2 text-sm text-green-700">
            <Clock className="w-4 h-4" />
            <span>Session expires in: {formatTimeUntilExpiry()}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {children}
    </div>
  );
};

export default EnhancedSecurityWrapper;
